import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_CONFIG from './config';
import sessionManager from './utils/sessionManager';
import Logo from './components/Logo';
import LoginOverlay from './components/LoginOverlay';
import ProfileValidationOverlay from './components/ProfileValidationOverlay';
import { validateProfileForRental, consolidateProfileData, formatProfileForAPI } from './utils/profileValidation';
import { Pagination, Box } from '@mui/material';

// --- Helper Functions ---

const isMapUrl = (url) => {
  if (!url) return false;
  const mapPatterns = [
    's3.amazonaws.com/maps/',
    '/maps/',
    'maps.googleapis.com',
    'map',
    'location'
  ];
  const lowerUrl = url.toLowerCase();
  return mapPatterns.some(pattern => lowerUrl.includes(pattern));
};

const cleanImageUrl = (url) => {
  if (!url) return '';
  if (typeof url === 'object' && url.url) {
    url = url.url;
  }
  url = String(url);
  if (url.includes('execute-api.us-east-1.amazonaws.com') || url.includes('s3.amazonaws.com')) {
    return url;
  }
  try {
    if (url.includes('%20') || url.includes('%2') || url.includes('%3')) {
      return decodeURIComponent(url);
    }
  } catch (e) {
    console.error('URL cleaning failed:', e);
  }
  return url;
};

// Pure JavaScript Lightbox - completely independent of React
class VanillaLightbox {
  constructor() {
    this.isOpen = false;
    this.photos = [];
    this.currentIndex = 0;
    this.container = null;
    this.eventListeners = [];
  }

  open(photos, index = 0) {
    console.log('Opening lightbox with', photos.length, 'photos, starting at index', index);
    this.photos = photos;
    this.currentIndex = index;
    this.isOpen = true;
    this.createLightbox();
    this.addGlobalEventListeners();
  }

  close() {
    console.log('Closing lightbox');
    this.isOpen = false;
    this.removeGlobalEventListeners();
    if (this.container) {
      document.body.removeChild(this.container);
      this.container = null;
    }
    document.body.style.overflow = '';
  }

  next() {
    if (this.currentIndex < this.photos.length - 1) {
      this.currentIndex++;
      this.updateImage();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateImage();
    }
  }

  createLightbox() {
    // Create container
    this.container = document.createElement('div');
    this.container.className = 'vanilla-lightbox-container';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 2147483647;
    `;

    // Create image container
    const imageContainer = document.createElement('div');
    imageContainer.style.cssText = `
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
    `;

    // Create image
    const img = document.createElement('img');
    img.style.cssText = `
      max-width: 100%;
      max-height: 90vh;
      object-fit: contain;
    `;
    img.src = cleanImageUrl(this.photos[this.currentIndex].url || this.photos[this.currentIndex]);
    img.alt = `Image ${this.currentIndex + 1}`;

    imageContainer.appendChild(img);

    // Create navigation buttons if more than one photo
    if (this.photos.length > 1) {
      // Previous button
      if (this.currentIndex > 0) {
        const prevBtn = this.createNavButton('←', 'left: 10px;', () => this.prev());
        imageContainer.appendChild(prevBtn);
      }

      // Next button
      if (this.currentIndex < this.photos.length - 1) {
        const nextBtn = this.createNavButton('→', 'right: 10px;', () => this.next());
        imageContainer.appendChild(nextBtn);
      }
    }

    // Create close button
    const closeBtn = document.createElement('div');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: white;
      font-size: 24px;
      font-family: Arial, sans-serif;
    `;
    closeBtn.onclick = () => this.close();

    this.container.appendChild(imageContainer);
    this.container.appendChild(closeBtn);

    // Add background click to close
    this.container.onclick = (e) => {
      if (e.target === this.container) {
        this.close();
      }
    };

    document.body.appendChild(this.container);
    document.body.style.overflow = 'hidden';
  }

  createNavButton(text, position, onClick) {
    const btn = document.createElement('div');
    btn.innerHTML = text;
    btn.style.cssText = `
      position: absolute;
      top: 50%;
      ${position}
      transform: translateY(-50%);
      background: rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: white;
      font-size: 24px;
      font-family: Arial, sans-serif;
    `;
    btn.onclick = (e) => {
      e.stopPropagation();
      onClick();
    };
    return btn;
  }

  updateImage() {
    if (!this.container) return;
    
    const img = this.container.querySelector('img');
    if (img) {
      img.src = cleanImageUrl(this.photos[this.currentIndex].url || this.photos[this.currentIndex]);
      img.alt = `Image ${this.currentIndex + 1}`;
    }

    // Update navigation buttons
    const imageContainer = this.container.querySelector('div');
    const existingNavBtns = imageContainer.querySelectorAll('div');
    existingNavBtns.forEach(btn => {
      if (btn.innerHTML === '←' || btn.innerHTML === '→') {
        btn.remove();
      }
    });

    // Re-add navigation buttons
    if (this.photos.length > 1) {
      if (this.currentIndex > 0) {
        const prevBtn = this.createNavButton('←', 'left: 10px;', () => this.prev());
        imageContainer.appendChild(prevBtn);
      }
      if (this.currentIndex < this.photos.length - 1) {
        const nextBtn = this.createNavButton('→', 'right: 10px;', () => this.next());
        imageContainer.appendChild(nextBtn);
      }
    }
  }

  addGlobalEventListeners() {
    // Keyboard navigation
    const keyHandler = (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    };
    document.addEventListener('keydown', keyHandler);
    this.eventListeners.push({ type: 'keydown', handler: keyHandler });

    // Touch handling
    let touchStart = null;
    let touchEnd = null;

    const touchStartHandler = (e) => {
      if (!this.isOpen || !this.container?.contains(e.target)) return;
      touchStart = e.touches[0].clientX;
      touchEnd = null;
    };

    const touchMoveHandler = (e) => {
      if (!this.isOpen || !this.container?.contains(e.target)) return;
      touchEnd = e.touches[0].clientX;
    };

    const touchEndHandler = (e) => {
      if (!this.isOpen || !this.container?.contains(e.target)) return;
      if (touchStart && touchEnd) {
        const diff = touchStart - touchEnd;
        if (Math.abs(diff) > 50) {
          if (diff > 0) this.next();
          else this.prev();
        }
      }
    };

    document.addEventListener('touchstart', touchStartHandler, { passive: true });
    document.addEventListener('touchmove', touchMoveHandler, { passive: true });
    document.addEventListener('touchend', touchEndHandler, { passive: true });

    this.eventListeners.push(
      { type: 'touchstart', handler: touchStartHandler },
      { type: 'touchmove', handler: touchMoveHandler },
      { type: 'touchend', handler: touchEndHandler }
    );

    // Prevent background interactions
    const preventBackgroundEvent = (e) => {
      if (!this.isOpen) return;
      if (this.container && !this.container.contains(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const events = ['click', 'touchstart', 'touchmove', 'touchend', 'mousedown', 'mouseup'];
    events.forEach(eventType => {
      document.addEventListener(eventType, preventBackgroundEvent, { capture: true, passive: false });
      this.eventListeners.push({ type: eventType, handler: preventBackgroundEvent, options: { capture: true, passive: false } });
    });
  }

  removeGlobalEventListeners() {
    this.eventListeners.forEach(({ type, handler, options }) => {
      document.removeEventListener(type, handler, options);
    });
    this.eventListeners = [];
  }
}

// Global lightbox instance
const globalLightbox = new VanillaLightbox();

const PhotoGallery = ({ photos, listingId, showThumbnails = true }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  if (!photos || photos.length === 0) {
    return (
      <div className="no-photos">
        <div className="no-photos-placeholder">
          🏠 No photos available
        </div>
      </div>
    );
  }

  const displayPhotos = photos.slice(0, 8); // Show up to 8 photos

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % displayPhotos.length);
    setImageLoaded(false);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + displayPhotos.length) % displayPhotos.length);
    setImageLoaded(false);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current && touchEndX.current) {
      const swipeDistance = touchStartX.current - touchEndX.current;
      if (swipeDistance > 50) {
        nextPhoto();
      } else if (swipeDistance < -50) {
        prevPhoto();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const currentPhoto = displayPhotos[currentPhotoIndex];

  return (
    <div className="photo-gallery">
      <div
        className="main-photo-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="photo-counter">
          {currentPhotoIndex + 1} / {displayPhotos.length}
          {photos.length > 8 && <span className="more-photos"> (+{photos.length - 8} more)</span>}
        </div>
        
        <div className="main-photo">
          {!imageLoaded && <div className="photo-loading">Loading...</div>}
          <img 
            src={currentPhoto.url} 
            alt={currentPhoto.description || `Photo ${currentPhotoIndex + 1}`}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.target.style.display = 'none';
              setImageLoaded(true);
            }}
            style={{ display: imageLoaded ? 'block' : 'none' }}
          />
        </div>
        
        {displayPhotos.length > 1 && (
          <>
            <button className="photo-nav prev" onClick={prevPhoto} aria-label="Previous photo">
              ‹
            </button>
            <button className="photo-nav next" onClick={nextPhoto} aria-label="Next photo">
              ›
            </button>
          </>
        )}

        {currentPhoto.description && (
          <div className="photo-description">
            {currentPhoto.description}
          </div>
        )}
      </div>

      {showThumbnails && displayPhotos.length > 1 && (
        <div className="photo-thumbnails">
          {displayPhotos.map((photo, index) => (
            <button
              key={photo.order || index}
              className={`thumbnail ${index === currentPhotoIndex ? 'active' : ''}`}
              onClick={() => {
                setCurrentPhotoIndex(index);
                setImageLoaded(false);
              }}
            >
              <img 
                src={photo.url} 
                alt={`Thumbnail ${index + 1}`}
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PhotoCarousel = ({ photos, messageId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  if (!photos || photos.length === 0) return null;
  
  const nextImage = () => setCurrentIndex((p) => (p + 1) % photos.length);
  const prevImage = () => setCurrentIndex((p) => (p - 1 + photos.length) % photos.length);
  const goToImage = (index) => setCurrentIndex(index);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) nextImage();
    else if (distance < -50) prevImage();
  };

  const handleImageClick = () => {
    // Use original URLs for lightbox to show uncropped images
    const originalPhotos = photos.map(photo => ({
      ...photo,
      url: photo.originalUrl || photo.url // Use originalUrl if available, fallback to url
    }));
    
    // Use the vanilla lightbox
    globalLightbox.open(originalPhotos, currentIndex);
  };
  
  const currentPhoto = photos[currentIndex];
  const isMap = isMapUrl(currentPhoto.url);
  
  return (
    <div className="photo-carousel">
      <div className="carousel-container">
        <div className="carousel-main-image" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
          <img 
            src={cleanImageUrl(currentPhoto.url)} 
            alt={isMap ? 'Property location map' : `Property image ${currentIndex + 1}`} 
            className="carousel-image"
            onClick={handleImageClick}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {isMap && <div className="map-indicator">📍 Map</div>}
          {photos.length > 1 && (
            <>
              <button className="carousel-nav prev" onClick={prevImage}>‹</button>
              <button className="carousel-nav next" onClick={nextImage}>›</button>
            </>
          )}
          {photos.length > 1 && <div className="carousel-counter">{currentIndex + 1} / {photos.length}</div>}
        </div>
        {photos.length > 1 && (
          <div className="carousel-thumbnails">
            {photos.map((photo, index) => {
              const thumbIsMap = isMapUrl(photo.url);
              return (
                <div key={index} className={`carousel-thumbnail ${index === currentIndex ? 'active' : ''}`} onClick={() => goToImage(index)}>
                  <img src={cleanImageUrl(photo.url)} alt={thumbIsMap ? 'Map' : `Thumbnail ${index + 1}`} onError={(e) => { e.target.style.display = 'none'; }} />
                  {thumbIsMap && <div className="thumbnail-map-icon">📍</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);
  const [removingFavorites, setRemovingFavorites] = useState(new Set());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [favorites, setFavorites] = useState(new Set());
  const [detailedViewListing, setDetailedViewListing] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  const [showProfileValidation, setShowProfileValidation] = useState(false);
  const [pendingApplicationListing, setPendingApplicationListing] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false); // Prevent duplicate initialization
  
  // Pagination state
  const [allListings, setAllListings] = useState([]);
  const listingsPerPage = 20;

  // Ref to prevent duplicate API calls in StrictMode
  const searchCallInProgress = useRef(false);

  const messagesContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle pagination page change
  const handlePageChange = (event, newPage) => {
    // Calculate the listings for the new page
    const startIndex = (newPage - 1) * listingsPerPage;
    const endIndex = startIndex + listingsPerPage;
    const newPageListings = allListings.slice(startIndex, endIndex);
    
    // Update only the listings message with new page data, leave action buttons unchanged
    setMessages(prevMessages => {
      return prevMessages.map(message => {
        if (message.listings && message.showPagination) {
          const totalCount = message.totalCount;
          const showingCount = Math.min(listingsPerPage, newPageListings.length);
          const messageText = `Found ${totalCount} listings for your search. Showing ${showingCount} listings (page ${newPage} of ${Math.ceil(totalCount / listingsPerPage)}):`;
          
          return {
            ...message,
            text: messageText,
            listings: newPageListings,
            currentPage: newPage
          };
        }
        return message;
      });
    });
  };
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
      if (isMobileView) {
        setViewMode('cards');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Also call on initial mount

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Prevent duplicate initialization in React StrictMode
    if (hasInitialized) return;
    
    // Check if user is authenticated using session manager
    const authenticated = sessionManager.isAuthenticated();
    const phoneNumber = sessionStorage.getItem('phoneNumber');
    
    if (!authenticated && !phoneNumber) {
      // No authentication, show login overlay
      setShowLoginOverlay(true);
      return;
    }
    
    setIsAuthenticated(authenticated);
    
    // Check if we have property search parameters
    const searchParams = new URLSearchParams(location.search);
    const propertyType = searchParams.get('property_type');
    const bedrooms = searchParams.get('bedrooms');
    const count = searchParams.get('count');
    const avgRent = searchParams.get('avg_rent');
    const locationDisplay = searchParams.get('location_display');
    const userRole = searchParams.get('user_role') || 'tenant'; // Default to tenant if not specified
    
    if (propertyType && bedrooms) {
      // Property search context - create contextual greeting
      const bedroomText = bedrooms === '0' ? 'bachelor/studio' : 
                          bedrooms === '1' ? '1 bedroom' : 
                          bedrooms === '5+' ? '5+ bedroom' : 
                          `${bedrooms} bedroom`;
      
      const locationText = locationDisplay || 'Greater Toronto Area';
      
      let contextualGreeting = `Hi! I'm Rental Help AI. I see you're a ${userRole} looking for ${bedroomText} ${propertyType.toLowerCase()}s in ${locationText}.`;
      
      if (count && avgRent && Number(avgRent) > 0) {
        contextualGreeting += `\n\n📊 **Market Summary:**\n• Found ${count} available properties\n• Average rent: $${Number(avgRent).toLocaleString()}/month\n• Data from Realtor.ca, Kijiji, and Facebook Marketplace`;
      } else if (count) {
        contextualGreeting += `\n\n📊 **Market Summary:**\n• Found ${count} available properties\n• Data from Realtor.ca, Kijiji, and Facebook Marketplace`;
      }
      
      // Set initial property search message and trigger search
      setMessages([
        {
          id: 1,
          text: contextualGreeting,
          sender: 'bot',
          timestamp: new Date(),
          buttons: [],
          photos: [],
          documents: []
        }
      ]);
      
      // Mark as initialized and trigger property search with current parameters
      setHasInitialized(true);
      setTimeout(() => {
        sendPropertySearchMessage(searchParams);
      }, 500);
      
    } else {
      // Default welcome message
      setMessages([
        {
          id: 1,
          text: "Welcome to Tenant Help AI in Greater Toronto Area! Are you looking for a place to rent or are you listing a property?",
          sender: 'bot',
          timestamp: new Date(),
          buttons: [
            [
              { text: "I'm looking to rent", callback_data: "role_tenant" },
              { text: "I'm listing a property", callback_data: "role_landlord" }
            ]
          ],
          photos: [],
          documents: []
        }
      ]);
      setHasInitialized(true);
    }
  }, [location.search, hasInitialized]); // Include hasInitialized to satisfy ESLint

  useEffect(() => {
    // Scroll to the first bot message after the last user message
    const scrollToNewMessages = () => {
      if (messages.length === 0) return;
      
      // Find the last user message index
      const lastUserIndex = [...messages].reverse().findIndex(msg => msg.sender === 'user');
      if (lastUserIndex === -1) return;
      
      // Get the first bot message after the last user message
      const firstNewBotMessage = messages[messages.length - lastUserIndex];
      if (!firstNewBotMessage) return;
      
      // Find the DOM element for this message and scroll to it
      const messageElements = document.querySelectorAll('.message');
      const targetElement = Array.from(messageElements).find(
        el => el.getAttribute('data-message-id') === String(firstNewBotMessage.id)
      );
      
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    scrollToNewMessages();
  }, [messages]);

  useEffect(() => {
    // Handle scroll indicator visibility
    const handleScroll = () => {
      if (!messagesContainerRef.current) return;
      
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNotAtTop = scrollTop < scrollHeight - clientHeight - 100; // Show when not near top
      const hasEnoughContent = scrollHeight > clientHeight + 200; // Only show if there's enough content
      
      setShowScrollIndicator(isNotAtTop && hasEnoughContent);
    };
    
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      messagesContainer.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
    }
    
    return () => {
      if (messagesContainer) {
        messagesContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [messages]); // Re-attach when messages change

  const formatMessageText = (text) => {
    if (!text) return '';
    
    // Convert HTML entities and formatting tags
    let formattedText = text
      // Handle HTML bold tags
      .replace(/<b>/g, '<strong>')
      .replace(/<\/b>/g, '</strong>')
      // Handle HTML italic tags  
      .replace(/<i>/g, '<em>')
      .replace(/<\/i>/g, '</em>')
      // Handle HTML underline tags
      .replace(/<u>/g, '<u>')
      .replace(/<\/u>/g, '</u>')
      // Handle line breaks
      .replace(/\n/g, '<br>')
      // Handle Telegram markdown-style formatting
      .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
      .replace(/_([^_]+)_/g, '<em>$1</em>')
      // Clean up any remaining backslash-b tags
      .replace(/\\b/g, '<strong>')
      .replace(/\\\/b/g, '</strong>');
    
    return formattedText;
  };

  // Function to get the appropriate icon and link based on listing source
  const getSourceIconAndLink = (listing) => {
    const source = listing.source?.toLowerCase();
    
    switch (source) {
      case 'facebook':
        return {
          iconUrl: '/facebook-marketplace.png',
          iconAlt: 'Facebook Marketplace',
          linkUrl: listing.listing_url || 'https://www.facebook.com/marketplace',
          linkTitle: 'View on Facebook Marketplace'
        };
      case 'kijiji':
        return {
          iconUrl: '/kijiji-icon-filled-128.webp',
          iconAlt: 'Kijiji',
          linkUrl: listing.listing_url || 'https://www.kijiji.ca',
          linkTitle: 'View on Kijiji'
        };
      case 'realtor':
      default:
        return {
          iconUrl: 'https://www.realtor.ca/images/en-ca/powered_by_realtor.svg',
          iconAlt: 'REALTOR.ca',
          linkUrl: listing.listing_url || 'https://www.realtor.ca/en',
          linkTitle: 'View on REALTOR.ca'
        };
    }
  };

  const formatPrice = (price) => {
    if (!price || price === '0' || parseInt(price) === 0) return 'Price on request';
    const numPrice = parseInt(price);
    if (isNaN(numPrice) || numPrice <= 0) return 'Price on request';
    return `$${numPrice.toLocaleString()}`;
  };

  // Sort function to sort listings by listed date
  const sortListings = (message, sortBy) => {
    if (sortBy === 'listed') {
      const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
      setSortDirection(newDirection);
      
      // Create a copy of messages and sort the specific message's listings
      const updatedMessages = messages.map(msg => {
        if (msg.id === message.id && msg.listings) {
          const sortedListings = [...msg.listings].sort((a, b) => {
            const dateA = new Date(a.date_listed || 0);
            const dateB = new Date(b.date_listed || 0);
            
            if (newDirection === 'desc') {
              return dateB.getTime() - dateA.getTime(); // Newest first (most recent date)
            } else {
              return dateA.getTime() - dateB.getTime(); // Oldest first (earliest date)
            }
          });
          
          return {
            ...msg,
            listings: sortedListings
          };
        }
        return msg;
      });
      
      setMessages(updatedMessages);
    }
  };



  // Action handler functions
  const toggleFavorite = (listingId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(listingId)) {
        newFavorites.delete(listingId);
      } else {
        newFavorites.add(listingId);
      }
      return newFavorites;
    });
  };

  const handleExternalView = (listing) => {
    const sourceInfo = getSourceIconAndLink(listing);
    const url = sourceInfo.linkUrl.startsWith('http') ? sourceInfo.linkUrl : `https://${sourceInfo.linkUrl}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDetailedView = (listing) => {
    setDetailedViewListing(listing);
  };

  const closeDetailedView = () => {
    setDetailedViewListing(null);
  };

  // Helper function to fetch complete user profile data
  const fetchUserProfile = async () => {
    try {
      const userInfo = sessionManager.getUserInfo();
      if (!userInfo || !userInfo.authToken) {
        return null;
      }

      // Fetch both basic profile and extended tenant profile data
      const [profileResponse, tenantResponse] = await Promise.all([
        fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_USER_PREFERENCES}`, {
          headers: {
            'Authorization': `Bearer ${userInfo.authToken}`
          }
        }),
        fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_TENANT_PROFILE}`, {
          headers: {
            'Authorization': `Bearer ${userInfo.authToken}`
          }
        })
      ]);

      let profileData = {};
      let tenantData = {};

      if (profileResponse.ok) {
        const result = await profileResponse.json();
        profileData = result.profile || {};
      }

      if (tenantResponse.ok) {
        const result = await tenantResponse.json();
        tenantData = result.profile || {};
      }

      // Consolidate all profile data
      const consolidatedProfile = consolidateProfileData(userInfo, {
        ...profileData,
        ...tenantData
      });

      console.log('🔍 Debug Profile Validation:');
      console.log('- Raw tenant data:', tenantData);
      console.log('- Consolidated profile:', consolidatedProfile);
      console.log('- Duration field specifically:', {
        'tenantData.duration_of_stay': tenantData.duration_of_stay,
        'consolidatedProfile.termDuration': consolidatedProfile.termDuration
      });
      console.log('- Missing fields:', validateProfileForRental(consolidatedProfile));

      return consolidatedProfile;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Helper function to save profile data
  const saveProfileData = async (profileData) => {
    try {
      const userInfo = sessionManager.getUserInfo();
      if (!userInfo || !userInfo.authToken) {
        throw new Error('Authentication required');
      }

      const formattedData = formatProfileForAPI(profileData);

      // Save basic profile data
      const profileResponse = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPDATE_USER_PROFILE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userInfo.authToken}`
        },
        body: JSON.stringify({
          firstName: formattedData.firstName,
          lastName: formattedData.lastName,
          email: formattedData.email
        })
      });

      if (!profileResponse.ok) {
        throw new Error('Failed to save basic profile');
      }

      // Save extended tenant profile data
      const tenantResponse = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPDATE_TENANT_PROFILE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userInfo.authToken}`
        },
        body: JSON.stringify(formattedData)
      });

      if (!tenantResponse.ok) {
        throw new Error('Failed to save tenant profile');
      }

      return true;
    } catch (error) {
      console.error('Error saving profile data:', error);
      throw error;
    }
  };

  const handleApplyToRental = async (listing) => {
    if (!listing || (listing.source?.toLowerCase() !== 'realtor' && listing.source !== 'Realtor')) {
      alert('Apply feature is only available for Realtor listings');
      return;
    }

    // Get auth token from session manager
    const userInfo = sessionManager.getUserInfo();
    if (!userInfo || !userInfo.authToken) {
      alert('Please log in to apply to listings');
      return;
    }

    setLoading(true);
    try {
      // Fetch current user profile data
      const currentProfile = await fetchUserProfile();
      
      // Validate profile completeness
      const missing = validateProfileForRental(currentProfile);
      
      // Extract agent name from listing
      const agentName = (listing.agent && (listing.agent.first_name || listing.agent.last_name)) ?
        `${listing.agent.first_name || ''}`.trim() :
        (listing.agent_details?.name?.split(' ')[0] || 'Listing Agent');
      
      // Always show validation overlay to let user review and edit the message
      setUserProfile({...currentProfile, agentName});
      setMissingFields(missing);
      setPendingApplicationListing(listing);
      setShowProfileValidation(true);
      setLoading(false);
    } catch (error) {
      console.error('Error preparing application:', error);
      alert('An error occurred while preparing your application. Please try again.');
      setLoading(false);
    }
  };

  // Function to actually submit the rental application
  const submitApplication = async (listing, customMessage = null, consentToShare = false) => {
    try {
      const userInfo = sessionManager.getUserInfo();
      const authToken = userInfo.authToken;

      console.log('Sending apply request:', {
        url: `${API_CONFIG.BASE_URL}/apply-to-rental`,
        listing_id: listing.id,
        has_auth_token: !!authToken,
        has_custom_message: !!customMessage
      });

      const requestBody = {
        listing_id: listing.id,
        auth_token: authToken,
        consent_to_share: consentToShare
      };

      // Add custom message if provided
      if (customMessage) {
        requestBody.message = customMessage;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/apply-to-rental`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Apply response status:', response.status);
      
      if (!response.ok) {
        console.error('Apply response not ok:', response.status, response.statusText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Apply response data:', result);

      if (result.success) {
        alert(result.message);
      } else {
        alert(result.message || 'Failed to send application');
      }
    } catch (error) {
      console.error('Error applying to rental:', error);
      alert('An error occurred while sending your application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle profile validation completion
  const handleProfileValidationComplete = async (completionData) => {
    try {
      setLoading(true);
      
      // Save the completed profile data
      await saveProfileData(completionData.profileData);
      
      // Close the validation overlay
      setShowProfileValidation(false);
      
      // Submit the application with the pending listing and the composed message
      if (pendingApplicationListing) {
        await submitApplication(pendingApplicationListing, completionData.message, completionData.consentToShare);
        setPendingApplicationListing(null);
      }
    } catch (error) {
      console.error('Error completing profile and applying:', error);
      alert('Error saving profile information. Please try again.');
      setLoading(false);
    }
  };

  // Handle profile validation cancellation
  const handleProfileValidationCancel = () => {
    setShowProfileValidation(false);
    setPendingApplicationListing(null);
    setLoading(false);
  };

     const sendMoreListings = async (searchParams, offset) => {
     setLoading(true);

     try {
       // Build search parameters for the property search endpoint
       const searchParamsObj = new URLSearchParams();
       
       // Add offset parameter
       searchParamsObj.append('offset', offset.toString());
       
       // Location filters - postal code takes precedence over city/area
       const postalCode = searchParams.get('postal_code');
       if (postalCode && postalCode.trim().length >= 3) {
         searchParamsObj.append('area_postal_code', postalCode.trim().toUpperCase());
       } else {
         // Use city + area filters (only when no postal code)
         const city = searchParams.get('city');
         if (city) {
           searchParamsObj.append('city', city);
         }
         
         const area = searchParams.get('neighborhood');
         if (area) {
           searchParamsObj.append('neighborhood', area);
         }
       }

       // Add property type and bedroom filters
       const propertyType = searchParams.get('property_type');
       if (propertyType) {
         searchParamsObj.append('property_type', propertyType);
       }

       const bedrooms = searchParams.get('bedrooms');
       if (bedrooms) {
         searchParamsObj.append('bedrooms', bedrooms);
       }

       const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/property-search?${searchParamsObj}`;

       const response = await fetch(url, {
         method: 'GET',
         headers: {
           'Content-Type': 'application/json',
         }
       });

       const data = await response.json();
       
       if (response.ok && data.success && data.data && data.data.listings) {
         // Create more listings message - show next 20 listings
         const listings = data.data.listings.slice(0, 20).map(l => {
           let photoArray = [];
           if (typeof l.media_urls === 'string') {
             try {
               // It might be a JSON string of an array
               photoArray = JSON.parse(l.media_urls);
             } catch (e) {
               // Or just a single URL string
               photoArray = [l.media_urls];
             }
           } else if (Array.isArray(l.media_urls)) {
             photoArray = l.media_urls;
           }

           // Ensure the final format is an array of objects like { url: '...' }
           const formattedPhotos = photoArray.map(url => (typeof url === 'string' ? { url } : url)).filter(p => p && p.url);

           return {
             ...l,
             photos: formattedPhotos,
           };
         });
         
         if (listings.length > 0) {
           // Auto-sort listings by newest first
           const sortedListings = [...listings].sort((a, b) => {
             const dateA = new Date(a.date_listed || 0);
             const dateB = new Date(b.date_listed || 0);
             return dateB.getTime() - dateA.getTime(); // Sort by newest first (most recent date)
           });
           
           // Create a message showing more listings
           const moreListingsMessage = {
             id: Date.now() + 1,
             text: `Here are ${listings.length} more listings (showing results ${offset + 1}-${offset + listings.length}):`,
             sender: 'bot',
             timestamp: new Date(),
             buttons: [],
             photos: [],
             documents: [],
             listings: sortedListings // Store sorted listings data for rendering
           };
           
           // Create updated action buttons message with new offset
           const newOffset = offset + 20;
           const actionButtonsMessage = {
             id: Date.now() + 2,
             text: "What would you like to do next?",
             sender: 'bot',
             timestamp: new Date(),
             buttons: [
               [
                 { text: "📋 Show More Listings", callback_data: "show_more_listings" },
                 { text: "🔍 Update Search Criteria", callback_data: "filter_criteria" }
               ],
               [
                 { text: "❤️ View Favorites", callback_data: "view_favorites" },
                 { text: "🔄 Reset Conversation", callback_data: "reset_conversation" }
               ]
             ],
             photos: [],
             documents: [],
             searchParams: searchParams, // Store search params for "show more"
             currentOffset: newOffset // Track current offset for next batch
           };
           
           setMessages(prev => [...prev, moreListingsMessage, actionButtonsMessage]);
         } else {
           const noMoreResultsMessage = {
             id: Date.now() + 1,
             text: "No more listings found. You've reached the end of the search results.",
             sender: 'bot',
             timestamp: new Date(),
             buttons: [
               [
                 { text: "🔍 Update Search Criteria", callback_data: "filter_criteria" },
                 { text: "❤️ View Favorites", callback_data: "view_favorites" }
               ],
               [
                 { text: "🔄 Reset Conversation", callback_data: "reset_conversation" }
               ]
             ],
             photos: []
           };
           setMessages(prev => [...prev, noMoreResultsMessage]);
         }
       } else {
         const errorMessage = {
           id: Date.now() + 1,
           text: "I'm sorry, I couldn't load more listings right now. Please try again.",
           sender: 'bot',
           timestamp: new Date(),
           buttons: [],
           photos: []
         };
         setMessages(prev => [...prev, errorMessage]);
       }
     } catch (error) {
       console.error('More listings error:', error);
       const errorMessage = {
         id: Date.now() + 1,
         text: "I'm sorry, there was an error loading more listings. Please try again.",
         sender: 'bot',
         timestamp: new Date(),
         buttons: [],
         photos: []
       };
       setMessages(prev => [...prev, errorMessage]);
     } finally {
       setLoading(false);
     }
   };

   const sendPropertySearchMessage = async (searchParams) => {
    // Prevent duplicate calls in React StrictMode
    if (searchCallInProgress.current) {
      return;
    }
    
    searchCallInProgress.current = true;
    setLoading(true);

    try {
      // Build search parameters for the listing-details endpoint
      const searchParamsObj = new URLSearchParams();
      
      // Location filters - postal code takes precedence over city/area
      const postalCode = searchParams.get('postal_code');
      if (postalCode && postalCode.trim().length >= 3) {
        searchParamsObj.append('area_postal_code', postalCode.trim().toUpperCase());
      } else {
        // Use city + area filters (only when no postal code)
        const city = searchParams.get('city');
        if (city) {
          searchParamsObj.append('city', city);
        }
        
        // Check both 'neighborhood' and 'area' parameters
        const area = searchParams.get('neighborhood') || searchParams.get('area');
        if (area) {
          searchParamsObj.append('neighborhood', area);
        }
      }

      // Add property type and bedroom filters
      const propertyType = searchParams.get('property_type');
      if (propertyType) {
        searchParamsObj.append('property_type', propertyType);
      }

      const bedrooms = searchParams.get('bedrooms');
      if (bedrooms) {
        searchParamsObj.append('bedrooms', bedrooms);
      }

      // Add price filters
      const minRent = searchParams.get('min_rent');
      if (minRent && minRent !== '0') {
        searchParamsObj.append('min_price', minRent);
      }

      const maxRent = searchParams.get('max_rent');
      if (maxRent && maxRent !== '0') {
        searchParamsObj.append('max_price', maxRent);
      }

      // Add date filter support
      const daysFilter = searchParams.get('days_filter');
      if (daysFilter && daysFilter !== 'all') {
        searchParamsObj.append('days_filter', daysFilter);
      }

      // For initial search, get all available listings to enable pagination
      // We'll fetch a larger limit to get total count, then paginate on frontend
      const count = searchParams.get('count');
      const limit = count && count !== '0' ? count : '100'; // Fetch more to enable pagination
      
      searchParamsObj.append('offset', '0');
      searchParamsObj.append('limit', limit);

      const url = `https://nk2lgsxkwf.execute-api.us-east-1.amazonaws.com/prod/property-search?${searchParamsObj}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success && data.data && data.data.listings) {
        // Store all listings for pagination
        const allListingsData = data.data.listings.map(l => {
          let photoArray = [];
          if (typeof l.media_urls === 'string') {
            try {
              // It might be a JSON string of an array
              photoArray = JSON.parse(l.media_urls);
            } catch (e) {
              // Or just a single URL string
              photoArray = [l.media_urls];
            }
          } else if (Array.isArray(l.media_urls)) {
            photoArray = l.media_urls;
          }

          // Ensure the final format is an array of objects like { url: '...' }
          const formattedPhotos = photoArray.map(url => (typeof url === 'string' ? { url } : url)).filter(p => p && p.url);

          return {
            ...l,
            photos: formattedPhotos,
          };
        });
        
        // Auto-sort all listings by newest first
        const sortedAllListings = [...allListingsData].sort((a, b) => {
          const dateA = new Date(a.date_listed || 0);
          const dateB = new Date(b.date_listed || 0);
          return dateB.getTime() - dateA.getTime(); // Sort by newest first (most recent date)
        });
        
        // Store all listings for pagination
        setAllListings(sortedAllListings);
        
        // Get current page listings (first 20)
        const currentPageListings = sortedAllListings.slice(0, listingsPerPage);
        
        if (sortedAllListings.length > 0) {
          // Create message with total count and current page info
          const totalCount = data.data.total_count || sortedAllListings.length;
          const showingCount = Math.min(listingsPerPage, sortedAllListings.length);
          const messageText = totalCount > listingsPerPage 
            ? `Found ${totalCount} listings for your search. Showing ${showingCount} listings (page 1 of ${Math.ceil(totalCount / listingsPerPage)}):`
            : `Found ${totalCount} listings for your search:`;
           
          const listingsMessage = {
            id: Date.now() + 1,
            text: messageText,
            sender: 'bot',
            timestamp: new Date(),
            buttons: [],
            photos: [],
            documents: [],
            listings: currentPageListings, // Store current page listings for rendering
            showPagination: totalCount > listingsPerPage,
            totalCount: totalCount,
            currentPage: 1,
            totalPages: Math.ceil(totalCount / listingsPerPage)
          };
           
           // Always show action buttons after listings
           const actionButtonsMessage = {
             id: Date.now() + 2,
             text: "What would you like to do next?",
             sender: 'bot',
             timestamp: new Date(),
             buttons: [
               [
                 { text: "🔍 Update Search Criteria", callback_data: "filter_criteria" },
                 { text: "❤️ View Favorites", callback_data: "view_favorites" }
               ],
               [
                 { text: "🔄 Reset Conversation", callback_data: "reset_conversation" }
               ]
             ],
             photos: [],
             documents: [],
             searchParams: searchParams, // Store search params for "show more"
             currentOffset: 20 // Track current offset for next batch
           };
           
           setMessages(prev => [...prev, listingsMessage, actionButtonsMessage]);
        } else {
          const noResultsMessage = {
            id: Date.now() + 1,
            text: "No listings found for your search criteria. Try adjusting your filters or search in a different area.",
            sender: 'bot',
            timestamp: new Date(),
            buttons: [],
            photos: []
          };
          setMessages(prev => [...prev, noResultsMessage]);
        }
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          text: "I'm sorry, I couldn't load the property listings right now. Please try again.",
          sender: 'bot',
          timestamp: new Date(),
          buttons: [],
          photos: []
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Property search error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "I'm sorry, there was an error loading the listings. Please try again.",
        sender: 'bot',
        timestamp: new Date(),
        buttons: [],
        photos: []
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      searchCallInProgress.current = false;
    }
  };

  const sendMessage = async (messageText, isCallback = false, callbackData = null) => {
    if (!messageText && !callbackData) return;
    
    // Only add user message for text input, not button clicks
    if (!isCallback) {
      const userMessage = {
        id: Date.now(),
        text: messageText,
        sender: 'user',
        timestamp: new Date(),
        buttons: [],
        photos: []
      };
      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');
    }
    
    setLoading(true);

    try {
      const userInfo = sessionManager.getUserInfo();
      const authToken = userInfo?.authToken || sessionStorage.getItem('authToken');
      
      const requestBody = {
        auth_token: authToken
      };
      
      // Add either message or callback_data based on type
      if (isCallback) {
        requestBody.callback_data = callbackData;
      } else {
        requestBody.message = messageText;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHAT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken || 'temp-token'}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      // Debug logging for response data
      console.log('Backend response:', data);
      console.log('Photos in response:', data.photos);
      console.log('Response success:', data.success);
      
      if (response.ok && data.success) {
        // Check if this is a remove favorite response
        if (data.remove_listing_id) {
          // Handle favorite removal with animation
          console.log('Removing listing', data.remove_listing_id, 'from favorites');
          
          // Add success message
          const successMessage = {
            id: Date.now() + 1,
            text: data.response || "✅ Property removed from your favorites!",
            sender: 'bot',
            timestamp: new Date(),
            buttons: [],
            photos: [],
            documents: []
          };
          setMessages(prev => [...prev, successMessage]);
          
          // Find and remove the listing message with animation
          setMessages(prev => prev.map(message => {
            // Check if this message contains the remove button for this listing
            const hasRemoveButton = message.buttons && message.buttons.some(row => 
              row.some(button => button.callback_data === `remove_favorite_${data.remove_listing_id}`)
            );
            
            if (hasRemoveButton) {
              // Mark message for removal animation
              return { ...message, removing: true };
            }
            return message;
          }));
          
          // Actually remove the message after animation
          setTimeout(() => {
            setMessages(prev => prev.filter(message => {
              const hasRemoveButton = message.buttons && message.buttons.some(row => 
                row.some(button => button.callback_data === `remove_favorite_${data.remove_listing_id}`)
              );
              return !hasRemoveButton;
            }));
          }, 500); // Match CSS animation duration
          
          return;
        }
        
        // Check if this is a favorites response with individual listings
        if (data.listings && Array.isArray(data.listings) && data.listings.length > 0) {
          // Handle favorites with multiple separate listings
          console.log('Processing favorites response with', data.listings.length, 'listings');
          
          // Only add header message if there's actual text content
          if (data.response && data.response.trim()) {
            const headerMessage = {
              id: Date.now() + 1,
              text: data.response,
              sender: 'bot',
              timestamp: new Date(),
              buttons: data.buttons || [],
              photos: [],
              documents: data.documents || []
            };
            setMessages(prev => [...prev, headerMessage]);
          }
          
          // Add each listing as a separate message
          data.listings.forEach((listing, index) => {
            const listingMessage = {
              id: Date.now() + 2 + index,
              text: listing.text,
              sender: 'bot',
              timestamp: new Date(),
              buttons: listing.buttons || [],
              photos: listing.photos || [],
              documents: []
            };
            setMessages(prev => [...prev, listingMessage]);
          });
        } else {
          // Handle regular responses
          const botMessage = {
            id: Date.now() + 1,
            text: data.response || data.reply || "",
            sender: 'bot',
            timestamp: new Date(),
            buttons: data.buttons || [],
            photos: data.photos || [],
            documents: data.documents || []
          };

          setMessages(prev => [...prev, botMessage]);
        }
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          text: data.message || "I'm sorry, I'm having trouble processing your request right now. Please try again.",
          sender: 'bot',
          timestamp: new Date(),
          buttons: [],
          photos: []
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "I'm sorry, there was a connection error. Please check your internet connection and try again.",
        sender: 'bot',
        timestamp: new Date(),
        buttons: [],
        photos: []
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };



     // Update the button click handler to use the new function
   const handleButtonClick = (callbackData, buttonText, messageId) => {
     // Special handling for show more listings
     if (callbackData === 'show_more_listings') {
       const message = messages.find(msg => msg.id === messageId);
       if (message && message.searchParams && message.currentOffset !== undefined) {
         sendMoreListings(message.searchParams, message.currentOffset);
       }
       return;
     }
     
     // Special handling for favorites view
     if (callbackData === 'view_favorites') {
       // Clear all messages before sending the request
       setMessages([]);
       // Send the request
       sendMessage(null, true, callbackData);
       return;
     }

    // Special handling for remove favorite buttons
    if (callbackData.startsWith('remove_favorite_')) {
      const listingId = callbackData.replace('remove_favorite_', '');
      setRemovingFavorites(prev => new Set(prev).add(listingId));
      
      // Don't show user message for remove favorites, just send the callback
      sendMessage(null, true, callbackData).finally(() => {
        setRemovingFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(listingId);
          return newSet;
        });
      });
      return;
    }
    
    // Hide buttons for this message after clicking (for action buttons)
    if (messageId) {
      setMessages(prev => prev.map(message => 
        message.id === messageId 
          ? { ...message, buttons: [] } 
          : message
      ));
    }
    
    // Show user's button choice for other buttons
    const userMessage = {
      id: Date.now(),
      text: buttonText,
      sender: 'user',
      timestamp: new Date(),
      buttons: [],
      photos: []
    };
    setMessages(prev => [...prev, userMessage]);
    
    // Send callback to backend
    sendMessage(null, true, callbackData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;
    sendMessage(inputMessage);
  };

  const handleLogout = () => {
    sessionManager.clearSession();
    navigate('/');
  };

  // Handle permanent header button actions
  const handleHeaderAction = (action) => {
    switch (action) {
      case 'reset':
        // Reset conversation
        setMessages([
          {
            id: 1,
            text: "Welcome to Tenant Help AI in Greater Toronto Area! Are you looking for a place to rent or are you listing a property?",
            sender: 'bot',
            timestamp: new Date(),
            buttons: [
              [
                { text: "I'm looking to rent", callback_data: "role_tenant" },
                { text: "I'm listing a property", callback_data: "role_landlord" }
              ]
            ],
            photos: [],
            documents: []
          }
        ]);
        break;
      case 'favorites':
        // Clear messages before viewing favorites
        setMessages([]);
        // Send favorites callback
        sendMessage(null, true, 'view_favorites');
        break;
      case 'search':
        // Clear messages before starting new search
        setMessages([]);
        // Send search callback
        sendMessage(null, true, 'filter_criteria');
        break;
      case 'profile':
        // Clear messages before viewing profile
        setMessages([]);
        // Send profile callback
        sendMessage(null, true, 'profile_setup');
        break;
      default:
        break;
    }
  };

  const renderButtons = (buttons, messageId) => {
    if (!buttons || buttons.length === 0) return null;
    
    // Check if any buttons are potential user responses (action buttons)
    // Now include Like/Dislike buttons as action buttons
    const hasActionButtons = buttons.some(row => 
      row.some(button => 
        !button.callback_data?.startsWith('remove_favorite_')
      )
    );
    
    return (
      <div className={`message-buttons ${hasActionButtons ? 'action-buttons' : ''}`}>
        {buttons.map((row, rowIndex) => {
          return (
            <div key={rowIndex} className="button-row">
              {row.map((button, buttonIndex) => (
                <button
                  key={buttonIndex}
                  className={`chat-button ${hasActionButtons && !button.callback_data?.startsWith('remove_favorite_') ? 'action-button' : ''} ${button.callback_data.startsWith('remove_favorite_') && removingFavorites.has(button.callback_data.replace('remove_favorite_', '')) ? 'loading' : ''}`}
                  onClick={() => handleButtonClick(button.callback_data, button.text, messageId)}
                  disabled={loading || (button.callback_data.startsWith('remove_favorite_') && removingFavorites.has(button.callback_data.replace('remove_favorite_', '')))}
                >
                  {button.callback_data.startsWith('remove_favorite_') && removingFavorites.has(button.callback_data.replace('remove_favorite_', '')) 
                    ? 'Removing...' 
                    : button.text}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPhotoAlbum = (photos, messageId) => {
    if (!photos || photos.length === 0) return null;
    
    return (
      <div className="message-photos">
        <PhotoCarousel photos={photos} messageId={messageId} />
      </div>
    );
  };

  const renderPhotos = (photos, messageId) => {
    if (!photos || photos.length === 0) return null;
    
    return (
      <>
        {renderPhotoAlbum(photos, messageId)}
        {/* Show caption only for the first photo or if there's only one */}
        {photos[0]?.caption && (
          <div 
            className="photo-caption"
            dangerouslySetInnerHTML={{ __html: formatMessageText(photos[0].caption) }}
            style={{ padding: '0 16px 12px 16px' }}
          />
        )}
      </>
    );
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="chat-container">
      {/* Login Overlay for unauthenticated users */}
      {showLoginOverlay && (
        <LoginOverlay 
          onClose={() => {
            setShowLoginOverlay(false);
            // Re-check authentication after modal closes
            const authenticated = sessionManager.isAuthenticated();
            if (authenticated) {
              setIsAuthenticated(true);
            }
          }}
        />
      )}
      
      {/* Chat interface - show even if not authenticated for overlay to work */}
      {!isAuthenticated && !showLoginOverlay && (
        <div className="auth-loading">
          <div>Loading...</div>
        </div>
      )}
      
      {isAuthenticated && (
        <>
      <div className="chat-header">
        <Logo 
          size={isMobile ? "small" : "medium"} 
          showText={!isMobile} 
          variant="dark" 
          onClick={() => navigate('/')} 
        />
        <div className="header-actions">
          <div className="dropdown-container" ref={dropdownRef}>
            <button 
              onClick={() => setDropdownOpen(!dropdownOpen)} 
              className="dropdown-toggle"
              title="More options"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="2"/>
                <circle cx="12" cy="12" r="2"/>
                <circle cx="12" cy="19" r="2"/>
              </svg>
            </button>
            {dropdownOpen && (
              <div className="dropdown-menu">
                <button onClick={() => { handleHeaderAction('reset'); setDropdownOpen(false); }} className="dropdown-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                  </svg>
                  Reset Conversation
                </button>
                <button onClick={() => { handleHeaderAction('favorites'); setDropdownOpen(false); }} className="dropdown-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                  View Favorites
                </button>
                <button onClick={() => { handleHeaderAction('search'); setDropdownOpen(false); }} className="dropdown-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                  </svg>
                  Search Criteria
                </button>
                <button onClick={() => { handleHeaderAction('profile'); setDropdownOpen(false); }} className="dropdown-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  Edit Profile
                </button>
                <div className="dropdown-divider"></div>
                <button onClick={() => { handleLogout(); setDropdownOpen(false); }} className="dropdown-item logout">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="messages-container" ref={messagesContainerRef}>
        {messages.map((message) => {
          // If the message contains listings, render them separately
          if (message.listings && message.listings.length > 0) {
            return (
              <React.Fragment key={message.id}>
                {/* Render the introductory text in a normal message bubble */}
                <div className="message bot-message">
                  <div className="message-wrapper">
                    <div className="message-content">
                      <div className="message-text-with-toggle">
                        <div
                          className="message-text"
                          dangerouslySetInnerHTML={{ __html: formatMessageText(message.text) }}
                        />
                        {!isMobile && (
                          <div className="view-mode-toggle-compact inline">
                            <button
                              className={`toggle-btn-compact ${viewMode === 'table' ? 'active' : ''}`}
                              onClick={() => setViewMode('table')}
                              title="List view"
                            >
                              ☰ List
                            </button>
                            <button
                              className={`toggle-btn-compact ${viewMode === 'cards' ? 'active' : ''}`}
                              onClick={() => setViewMode('cards')}
                              title="Card view"
                            >
                              ⊞ Card
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Render the listings in a separate, full-width container */}
                <div className="listings-display-container">
                  <style jsx>{`
                    .message-text-with-toggle {
                      display: flex;
                      align-items: center;
                      gap: 12px;
                      flex-wrap: wrap;
                    }
                    
                    .view-mode-toggle-compact {
                      display: flex;
                      justify-content: center;
                      margin: 8px 0;
                    }
                    
                    .view-mode-toggle-compact.inline {
                      margin: 0;
                      flex-shrink: 0;
                    }
                    
                    .toggle-btn-compact {
                      background: rgba(255, 255, 255, 0.1);
                      border: 1px solid rgba(255, 255, 255, 0.2);
                      color: #ffffff;
                      padding: 6px 12px;
                      margin: 0 4px;
                      border-radius: 6px;
                      cursor: pointer;
                      font-size: 14px;
                      transition: all 0.2s ease;
                      min-width: 70px;
                      height: 32px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      gap: 4px;
                    }
                    
                    .toggle-btn-compact:hover {
                      background: rgba(255, 255, 255, 0.2);
                      border-color: rgba(255, 255, 255, 0.3);
                    }
                    
                    .toggle-btn-compact.active {
                      background: #007bff;
                      border-color: #007bff;
                      color: white;
                    }
                  `}</style>

                  {/* Pagination Controls */}
                  {message.showPagination && (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        my: 2,
                        '& .MuiPagination-root': {
                          '& .MuiPaginationItem-root': {
                            color: '#ffffff',
                            borderColor: '#444',
                            '&:hover': {
                              backgroundColor: '#333',
                            },
                            '&.Mui-selected': {
                              backgroundColor: '#0066cc',
                              color: '#ffffff',
                              '&:hover': {
                                backgroundColor: '#0052a3',
                              },
                            },
                          },
                        },
                      }}
                    >
                      <Pagination
                        count={message.totalPages}
                        page={message.currentPage || 1}
                        onChange={handlePageChange}
                        color="primary"
                        size="medium"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                  
                  {viewMode === 'table' && !isMobile && (
                    <div className="listings-table-container">
                      <div className="table-responsive">
                        <table className="listings-table">
                          <thead>
                            <tr>
                              <th>Property</th>
                              <th>Address</th>
                              <th>Type</th>
                              <th>Bed/Bath</th>
                              <th>Price</th>
                              <th>Source</th>
                              <th>Agent</th>
                              <th>
                                Listed
                                <button 
                                  className="sort-btn"
                                  onClick={() => sortListings(message, 'listed')}
                                  title={`Sort by listing date (${sortDirection === 'desc' ? 'newest first' : 'oldest first'})`}
                                >
                                  {sortDirection === 'desc' ? '↓' : '↑'}
                                </button>
                              </th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {message.listings.map((listing, index) => {
                              const sourceInfo = getSourceIconAndLink(listing);
                              return (
                                <tr key={listing.id || index} className="listing-row">
                                  <td className="property-cell">
                                    <div className="table-property-info">
                                      {listing.photos && listing.photos.length > 0 ? (
                                        <img 
                                          src={listing.photos[0].url} 
                                          alt="Property"
                                          className="table-property-thumb"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      ) : (
                                        <div className="table-property-placeholder">🏠</div>
                                      )}
                                      <div className="table-property-details">
                                        <div className="table-property-type">{listing.property_type || 'N/A'}</div>
                                        {listing.size_interior && (
                                          <div className="table-property-size">{listing.size_interior}</div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="address-cell">
                                    <div className="table-address">{listing.address || 'Address not available'}</div>
                                    <div className="table-location">
                                      {listing.neighborhood && listing.neighborhood !== 'Unknown Area' && `${listing.neighborhood}, `}
                                      {listing.city || 'Unknown City'}
                                      {listing.postal_code && ` ${listing.postal_code}`}
                                    </div>
                                  </td>
                                  <td className="type-cell">
                                    <div className="table-type">{listing.property_type || 'N/A'}</div>
                                  </td>
                                  <td className="bed-bath-cell">
                                    <div className="table-bed-bath">
                                      <div>🛏️ {listing.bedrooms || '0'}</div>
                                      <div>🚿 {listing.bathrooms || '0'}</div>
                                      {listing.parking_spaces && <div>🅿️ {listing.parking_spaces}</div>}
                                    </div>
                                  </td>
                                  <td className="price-cell">
                                    <div className="table-price">{formatPrice(listing.price)}</div>
                                    {listing.price && parseInt(listing.price) > 0 && (
                                      <div className="table-price-period">/month</div>
                                    )}
                                  </td>
                                  <td className="source-cell">
                                    <a 
                                      href={sourceInfo.linkUrl.startsWith('http') ? sourceInfo.linkUrl : `https://${sourceInfo.linkUrl}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="table-source-link"
                                      title={sourceInfo.linkTitle}
                                    >
                                      <img 
                                        width="40" 
                                        src={sourceInfo.iconUrl} 
                                        alt={sourceInfo.iconAlt}
                                        className="table-source-icon"
                                        onError={(e) => {
                                          if (listing.source?.toLowerCase() === 'facebook') {
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0iIzQyNjdCMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSI2IiBmaWxsPSIjNDI2N0IyIi8+CiAgPHRleHQgeD0iMjAiIHk9IjI2IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjAiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+ZjwvdGV4dD4KPC9zdmc+';
                                            e.target.alt = 'Facebook';
                                          } else if (listing.source?.toLowerCase() === 'kijiji') {
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0iIzAwNzFjZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSI2IiBmaWxsPSIjMDA3MWNlIi8+CiAgPHRleHQgeD0iMjAiIHk9IjI2IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SzwvdGV4dD4KPC9zdmc+';
                                            e.target.alt = 'Kijiji';
                                          } else {
                                            e.target.src = 'https://www.realtor.ca/images/en-ca/powered_by_realtor.svg';
                                            e.target.alt = 'REALTOR.ca';
                                          }
                                        }}
                                      />
                                    </a>
                                  </td>
                                  <td className="agent-cell">
                                    <div className="table-agent">
                                      {(() => {
                                        const agentName = (listing.agent && (listing.agent.first_name || listing.agent.last_name)) ?
                                          `${listing.agent.first_name || ''} ${listing.agent.last_name || ''}`.trim() :
                                          (listing.agent_details?.name || 'N/A');
                                        return <div className="table-agent-name">{agentName}</div>;
                                      })()}
                                    </div>
                                  </td>
                                  <td className="listed-cell">
                                    <div className="table-listed">
                                      {listing.date_listed && (
                                        <div className="table-days-on-market">
                                          {(() => {
                                            const days = Math.floor((new Date() - new Date(listing.date_listed)) / (1000 * 60 * 60 * 24));
                                            return days === 0 ? '0 Days' : `${days} Days`;
                                          })()}
                                        </div>
                                      )}
                                      {listing.origin_post_id && (
                                        <div className="table-listing-id">ID: {listing.origin_post_id}</div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="actions-cell">
                                    <div className="action-buttons">
                                      {(listing.source?.toLowerCase() === 'realtor' || listing.source === 'Realtor') && (
                                        <button
                                          className="action-btn apply-btn"
                                          onClick={() => handleApplyToRental(listing)}
                                          title="Apply to this rental"
                                        >
                                          📝
                                        </button>
                                      )}
                                      <button
                                        className={`action-btn favorite-btn ${favorites.has(listing.id) ? 'favorited' : ''}`}
                                        onClick={() => toggleFavorite(listing.id)}
                                        title={favorites.has(listing.id) ? 'Remove from favorites' : 'Add to favorites'}
                                      >
                                        {favorites.has(listing.id) ? '❤️' : '🤍'}
                                      </button>
                                      <button
                                        className="action-btn external-btn"
                                        onClick={() => handleExternalView(listing)}
                                        title="View on source website"
                                      >
                                        🔗
                                      </button>
                                      <button
                                        className="action-btn details-btn"
                                        onClick={() => handleDetailedView(listing)}
                                        title="View detailed information"
                                      >
                                        👁️
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {(viewMode === 'cards' || isMobile) && (
                    <div className="card-view-wrapper">
                      <div className="listings-grid">
                        {message.listings.map((listing, index) => (
                          <div key={listing.id || index} className="listing-card compact">
                            <PhotoGallery photos={listing.photos} listingId={listing.id} />
                            <div className="listing-content">
                              <div className="compact-price-header">
                                <div className="compact-price">
                                  {formatPrice(listing.price)}
                                  {listing.price && parseInt(listing.price) > 0 && <span className="price-period">/month</span>}
                                </div>
                                {(() => {
                                  const sourceInfo = getSourceIconAndLink(listing);
                                  return (
                                    <a 
                                      href={sourceInfo.linkUrl.startsWith('http') ? sourceInfo.linkUrl : `https://${sourceInfo.linkUrl}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="compact-source-link"
                                      title={sourceInfo.linkTitle}
                                    >
                                      <img 
                                        width="60" 
                                        src={sourceInfo.iconUrl} 
                                        alt={sourceInfo.iconAlt}
                                        onError={(e) => {
                                          if (listing.source?.toLowerCase() === 'facebook') {
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0iIzQyNjdCMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHJ4PSI4IiBmaWxsPSIjNDI2N0IyIi8+CiAgPHRleHQgeD0iMzAiIHk9IjM4IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMzAiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+ZjwvdGV4dD4KPC9zdmc+';
                                            e.target.alt = 'Facebook';
                                          } else if (listing.source?.toLowerCase() === 'kijiji') {
                                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0iIzAwNzFjZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHJ4PSI4IiBmaWxsPSIjMDA3MWNlIi8+CiAgPHRleHQgeD0iMzAiIHk9IjM4IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SzwvdGV4dD4KPC9zdmc+';
                                            e.target.alt = 'Kijiji';
                                          } else {
                                            e.target.src = 'https://www.realtor.ca/images/en-ca/powered_by_realtor.svg';
                                            e.target.alt = 'REALTOR.ca';
                                          }
                                        }}
                                      />
                                    </a>
                                  );
                                })()}
                              </div>
                              <div className="compact-address-section">
                                <h3 className="compact-address">
                                  {listing.address || 'Address not available'}
                                  {listing.postal_code && ` ${listing.postal_code}`}
                                </h3>
                              </div>
                              <div className="compact-location">
                                📍 {(() => {
                                  const parts = [];
                                  if (listing.neighborhood && listing.neighborhood !== 'Unknown Area') parts.push(listing.neighborhood);
                                  if (listing.city) parts.push(listing.city);
                                  if (listing.province) parts.push(listing.province);
                                  return parts.length > 0 ? parts.join(', ') : 'Location not available';
                                })()}
                              </div>
                              <div className="compact-property-info">
                                <div className="info-icon">
                                  🏠 <span>{listing.property_type || 'N/A'}</span>
                                </div>
                                {listing.bedrooms && (
                                  <div className="info-icon">
                                    🛏️ <span>{listing.bedrooms}</span>
                                  </div>
                                )}
                                {listing.bathrooms && (
                                  <div className="info-icon">
                                    🚿 <span>{listing.bathrooms}</span>
                                  </div>
                                )}
                                {listing.parking_spaces && (
                                  <div className="info-icon">
                                    🅿️ <span>{listing.parking_spaces}</span>
                                  </div>
                                )}
                              </div>
                              {listing.description && (
                                <div className="compact-description">
                                  {listing.description}
                                </div>
                              )}
                              {(() => {
                                const photoUrl = listing.agent?.photo_url || listing.agent_details?.photo_url || null;
                                const agentName = (listing.agent && (listing.agent.first_name || listing.agent.last_name)) ?
                                  `${listing.agent.first_name || ''} ${listing.agent.last_name || ''}`.trim() :
                                  (listing.agent_details?.name || 'Agent Name Not Available');
                                const officeName = listing.agent?.office_name || listing.agent_details?.office_name || 'Office Not Available';
                                return (
                                  <div className="compact-agent">
                                    <div className="agent-info-compact">
                                      {photoUrl && (
                                        <img
                                          src={photoUrl}
                                          alt={agentName}
                                          className="agent-photo-small"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      )}
                                      <div className="agent-text">
                                        <div className="agent-name-compact">{agentName}</div>
                                        <div className="agent-office-compact">{officeName}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                              <div className="compact-footer">
                                <div className="footer-left">
                                  {(listing.listing_id || listing.mls) && (
                                    <span className="compact-mls">MLS®: {listing.listing_id || listing.mls}</span>
                                  )}
                                  {listing.origin_post_id && (
                                    <span className="compact-ddf" style={{color: '#007bff', display: 'inline-block', marginLeft: '8px'}}>
                                      ID: {listing.origin_post_id}
                                    </span>
                                  )}
                                </div>
                                <div className="footer-right">
                                  {listing.date_listed && (
                                    <span className="compact-days-on-market">
                                      Days on Market: {(() => {
                                        const days = Math.floor((new Date() - new Date(listing.date_listed)) / (1000 * 60 * 60 * 24));
                                        return days === 0 ? '0 Days' : `${days} Days`;
                                      })()}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {(listing.source?.toLowerCase() === 'realtor' || listing.source === 'Realtor') && (
                                <div className="compact-apply">
                                  <button
                                    className="apply-rental-btn"
                                    onClick={() => handleApplyToRental(listing)}
                                    title="Apply to this rental"
                                  >
                                    📝 Apply
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          }

          // Render a standard message if it has no listings
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
            >
              <div className="message-wrapper">
                <div className="message-content">
                  {renderPhotos(message.photos, message.id)}
                  {message.text && (
                    <div 
                      className="message-text"
                      dangerouslySetInnerHTML={{ __html: formatMessageText(message.text) }}
                    />
                  )}
                  {message.buttons && message.buttons.length > 0 && message.buttons.every(row => 
                    row.every(button => button.callback_data && button.callback_data.startsWith('remove_favorite_'))
                  ) && renderButtons(message.buttons, message.id)}
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              {message.buttons && message.buttons.length > 0 && message.buttons.some(row => 
                row.some(button => !button.callback_data || !button.callback_data.startsWith('remove_favorite_'))
              ) && (
                <div className="external-action-buttons">
                  {renderButtons(message.buttons, message.id)}
                </div>
              )}
            </div>
          );
        })}
        {loading && (
          <div className="message bot-message">
            <div className="message-content typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}
      </div>

      {/* Scroll indicator */}
      <div 
        className={`scroll-indicator ${showScrollIndicator ? 'visible' : ''}`}
        onClick={scrollToBottom}
        title="Scroll to bottom"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>
      </div>

      <form onSubmit={handleSubmit} className="message-input-form">
        <div className="modern-input-container">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type your message here..."
            disabled={loading}
            className="modern-message-input"
          />
          <div className="input-actions">
            <button 
              type="button" 
              onClick={() => handleHeaderAction('favorites')} 
              className="input-action-button"
              title="View Favorites"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            </button>
            <button 
              type="button" 
              onClick={() => handleHeaderAction('reset')} 
              className="input-action-button"
              title="Reset Conversation"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
            </button>
            <button 
              type="button" 
              onClick={() => handleHeaderAction('search')} 
              className="input-action-button"
              title="Search Criteria"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            </button>
            <button 
              type="submit" 
              disabled={loading || !inputMessage.trim()} 
              className="modern-send-button"
              title="Send Message"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>
        </div>
      </form>
      </>
      )}

      {/* Detailed View Modal */}
      {detailedViewListing && (
        <div className="modal-overlay" onClick={closeDetailedView}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Close button positioned absolutely */}
            <button className="modal-close-absolute" onClick={closeDetailedView}>
              ✕
            </button>
            
            <div className="modal-body">
              {/* Photo Gallery */}
              <div className="modal-gallery">
                <PhotoGallery 
                  photos={detailedViewListing.photos} 
                  listingId={detailedViewListing.id} 
                  showThumbnails={false}
                />
                {/* Description integrated into gallery */}
                {detailedViewListing.description && (
                  <div className="gallery-description">
                    {detailedViewListing.description}
                  </div>
                )}
              </div>
              
              {/* Property Information */}
              <div className="modal-info">
                <div className="modal-info-section">
                  <h3>📍 Address & Location</h3>
                  <div className="info-item">
                    <strong>Address:</strong>
                    <span className="info-item-value">{detailedViewListing.address || 'Address not available'}</span>
                  </div>
                  {detailedViewListing.postal_code && (
                    <div className="info-item">
                      <strong>Postal Code:</strong>
                      <span className="info-item-value">{detailedViewListing.postal_code}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <strong>Neighborhood:</strong>
                    <span className="info-item-value">{detailedViewListing.neighborhood || 'Not specified'}</span>
                  </div>
                  <div className="info-item">
                    <strong>City:</strong>
                    <span className="info-item-value">{detailedViewListing.city || 'Unknown City'}</span>
                  </div>
                  <div className="info-item">
                    <strong>Province:</strong>
                    <span className="info-item-value">{detailedViewListing.province || 'ON'}</span>
                  </div>
                  
                  {/* Agent information integrated into this card */}
                  {(() => {
                    const agentName = (detailedViewListing.agent && (detailedViewListing.agent.first_name || detailedViewListing.agent.last_name)) ?
                      `${detailedViewListing.agent.first_name || ''} ${detailedViewListing.agent.last_name || ''}`.trim() :
                      (detailedViewListing.agent_details?.name || '');
                    const officeName = detailedViewListing.agent?.office_name || detailedViewListing.agent_details?.office_name || '';
                    const agentPhone = detailedViewListing.agent?.phone || detailedViewListing.agent_details?.phone;
                    
                    // Only show agent info if we have meaningful agent information
                    if (agentName && agentName !== 'Agent Name Not Available' && agentName.trim() !== '') {
                      return (
                        <>
                          <div className="agent-divider"></div>
                          <div className="info-item">
                            <strong>Agent:</strong>
                            <span className="info-item-value">{agentName}</span>
                          </div>
                          {officeName && officeName !== 'Office Not Available' && (
                            <div className="info-item">
                              <strong>Office:</strong>
                              <span className="info-item-value">{officeName}</span>
                            </div>
                          )}
                          {agentPhone && (
                            <div className="info-item">
                              <strong>Phone:</strong>
                              <span className="info-item-value">{agentPhone}</span>
                            </div>
                          )}
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div className="modal-info-section">
                  <h3>🏠 Property Details</h3>
                  <div className="info-item">
                    <strong>Property Type:</strong>
                    <span className="info-item-value">{detailedViewListing.property_type || 'Not specified'}</span>
                  </div>
                  <div className="info-item">
                    <strong>Bedrooms:</strong>
                    <span className="info-item-value">{detailedViewListing.bedrooms || 'Not specified'}</span>
                  </div>
                  <div className="info-item">
                    <strong>Bathrooms:</strong>
                    <span className="info-item-value">{detailedViewListing.bathrooms || 'Not specified'}</span>
                  </div>
                  <div className="info-item">
                    <strong>Parking Spaces:</strong>
                    <span className="info-item-value">{detailedViewListing.parking_spaces || 'Not specified'}</span>
                  </div>
                  {detailedViewListing.size_interior && (
                    <div className="info-item">
                      <strong>Interior Size:</strong>
                      <span className="info-item-value">{detailedViewListing.size_interior}</span>
                    </div>
                  )}
                  {detailedViewListing.furnished && (
                    <div className="info-item">
                      <strong>Furnished:</strong>
                      <span className="info-item-value">{detailedViewListing.furnished}</span>
                    </div>
                  )}
                  {detailedViewListing.pet_friendly && (
                    <div className="info-item">
                      <strong>Pet Friendly:</strong>
                      <span className="info-item-value">{detailedViewListing.pet_friendly}</span>
                    </div>
                  )}
                  {detailedViewListing.rental_term && (
                    <div className="info-item">
                      <strong>Rental Term:</strong>
                      <span className="info-item-value">{detailedViewListing.rental_term}</span>
                    </div>
                  )}
                </div>

                <div className="modal-info-section">
                  <h3>💰 Pricing & Source</h3>
                  <div className="info-item">
                    <strong>Monthly Rent:</strong>
                    <span className="info-item-value">
                      {formatPrice(detailedViewListing.price)}
                      {detailedViewListing.price && parseInt(detailedViewListing.price) > 0 && '/month'}
                    </span>
                  </div>
                  <div className="info-item">
                    <strong>Date Listed:</strong>
                    <span className="info-item-value">{detailedViewListing.date_listed || 'Not available'}</span>
                  </div>
                  {detailedViewListing.date_listed && (
                    <div className="info-item">
                      <strong>Days on Market:</strong>
                      <span className="info-item-value">
                        {(() => {
                          const days = Math.floor((new Date() - new Date(detailedViewListing.date_listed)) / (1000 * 60 * 60 * 24));
                          return days === 0 ? 'Listed today' : `${days} days`;
                        })()}
                      </span>
                    </div>
                  )}
                  <div className="info-item">
                    <strong>Source:</strong>
                    <span className="info-item-value">{detailedViewListing.source || 'Unknown'}</span>
                  </div>
                  {detailedViewListing.origin_post_id && (
                    <div className="info-item">
                      <strong>Listing ID:</strong>
                      <span className="info-item-value">{detailedViewListing.origin_post_id}</span>
                    </div>
                  )}
                  {detailedViewListing.listing_url && (
                    <div className="info-item">
                      <strong>Original Listing:</strong>
                      <span className="info-item-value">
                        <a 
                          href={detailedViewListing.listing_url.startsWith('http') ? detailedViewListing.listing_url : `https://${detailedViewListing.listing_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          View on {detailedViewListing.source} ↗
                        </a>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Validation Overlay */}
      <ProfileValidationOverlay
        isOpen={showProfileValidation}
        onClose={handleProfileValidationCancel}
        onComplete={handleProfileValidationComplete}
        missingFields={missingFields}
        userProfile={userProfile}
        listing={pendingApplicationListing}
      />
    </div>
  );
};

export default ChatPage; 